import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";

const SESSION_KEY = "sa_session_id";
const VISITOR_KEY = "sa_visitor_known";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, "");
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function isNewVisitor(): boolean {
  try {
    if (localStorage.getItem(VISITOR_KEY)) return false;
    localStorage.setItem(VISITOR_KEY, "1");
    return true;
  } catch {
    return true;
  }
}

async function postTrack(payload: any, useBeacon = false, accessToken?: string | null): Promise<Response | null> {
  const body = JSON.stringify(payload);
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon("/api/public/track", new Blob([body], { type: "application/json" }));
    return null;
  }
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  return fetch("/api/public/track", {
    method: "POST",
    headers,
    body,
    keepalive: true,
  }).catch(() => null);
}

export function VisitTracker() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const visitIdRef = useRef<string | null>(null);
  const startedAtRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    const sessionId = getSessionId();
    const newVisitor = isNewVisitor();

    const start = async () => {
      // skip admin pages from tracking
      if (path.startsWith("/admin") || path.startsWith("/_authenticated/admin")) return;
      let accessToken: string | null = null;
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data } = await supabase.auth.getSession();
        accessToken = data.session?.access_token ?? null;
      } catch { /* ignore */ }
      if (cancelled) return;
      startedAtRef.current = Date.now();
      const res = await postTrack({
        action: "start",
        session_id: sessionId,
        path,
        is_new_visitor: newVisitor,
        referrer: document.referrer || null,
      }, false, accessToken);
      if (cancelled || !res) return;
      try {
        const j = await res.json();
        if (j?.visit_id) visitIdRef.current = j.visit_id;
      } catch { /* ignore */ }
    };

    const end = (useBeacon = false) => {
      const id = visitIdRef.current;
      if (!id || !startedAtRef.current) return;
      const duration = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
      visitIdRef.current = null;
      void postTrack({ action: "end", visit_id: id, duration_seconds: duration }, useBeacon);
    };

    void start();

    const onHide = () => { if (document.visibilityState === "hidden") end(true); };
    const onBeforeUnload = () => end(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onBeforeUnload);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onBeforeUnload);
      end(false);
    };
  }, [path]);

  return null;
}
